import React, { useState, useEffect } from 'react';
import { postAPI, postAPIForm } from '../../../../app/api';
import editIcon from "../../../../theme/assets/images/icon/edit-icon.svg"
import deleteIcon from "../../../../theme/assets/images/icon/delete-icon.svg"
import Swal from 'sweetalert2';

export default function FooterSectionsManager() {
    const [footerSections, setFooterSections] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [selectedPosition, setSelectedPosition] = useState(1);
    
    const [formData, setFormData] = useState({
        section_type: 'text',
        content: '',
        link_url: '',
        icon_class: '',
        image_url: null,
        text_color: '#ffffff',
        font_family: 'Arial',
        font_size: '14',
        font_weight: 'normal',
        position: 1,
        order_index: 1,
        is_active: 1
    });

    // Available positions for footer sections
    const availablePositions = [
        { value: 1, label: 'Column 1 (Logo Area)' },
        { value: 2, label: 'Column 2 (Links Area)' },
        { value: 3, label: 'Column 3 (Contact Area)' },
        { value: 4, label: 'Column 4 (Social/Extra Area)' }
    ];

    // Section types
    const sectionTypes = [
        { value: 'heading', label: 'Heading' },
        { value: 'text', label: 'Plain Text' },
        { value: 'link', label: 'Text with Link' },
        { value: 'icon_link', label: 'Icon with Link' },
        { value: 'image', label: 'Image' }
    ];

    // Font families
    const fontFamilies = [
        'Arial', 'Helvetica', 'Georgia', 'Times New Roman', 
        'Verdana', 'Trebuchet MS', 'Impact', 'Comic Sans MS'
    ];

    // Font weights
    const fontWeights = [
        { value: 'normal', label: 'Normal' },
        { value: 'bold', label: 'Bold' },
        { value: 'lighter', label: 'Light' },
        { value: '600', label: 'Semi Bold' }
    ];

    // Fetch all footer sections
    useEffect(() => {
        fetchFooterSections();
    }, []);

    const fetchFooterSections = async () => {
        try {
            setLoading(true);
            const response = await postAPI('footer-data/list', {});
            if (response?.status === 1) {
                setFooterSections(response?.data || []);
            }
        } catch (error) {
            console.error('Error fetching footer sections:', error);
            Swal.fire('Error', 'Failed to fetch footer sections', 'error');
        } finally {
            setLoading(false);
        }
    };

    // Handle form changes
    const handleChange = (e) => {
        const { name, value, files } = e.target;
        const newValue = files ? files[0] : value;
        setFormData(prev => {
            const updated = {
                ...prev,
                [name]: newValue
            };
            return updated;
        });
    };

    // Get sections for selected position
    const getSectionsForPosition = () => {
        return footerSections.filter(s => s.position === selectedPosition && s.is_active === 1) || [];
    };

    // Edit footer section
    const handleEditSection = (section) => {
        console.log('📖 Loading footer section for edit:', section);
        setEditingId(section.id);
        setSelectedPosition(section.position);
        setFormData({
            section_type: section.section_type || 'text',
            content: section.content || '',
            link_url: section.link_url || '',
            icon_class: section.icon_class || '',
            image_url: section.image_url || null,
            text_color: section.text_color || '#ffffff',
            font_family: section.font_family || 'Arial',
            font_size: section.font_size || '14',
            font_weight: section.font_weight || 'normal',
            position: section.position || 1,
            order_index: section.order_index || 1,
            is_active: section.is_active !== undefined ? section.is_active : 1
        });
        setShowForm(true);
    };

    // Save footer section
    const handleSaveSection = async (e) => {
        e.preventDefault();
        
        if (!formData.content) {
            Swal.fire('Validation Error', 'Content is required', 'warning');
            return;
        }

        try {
            setLoading(true);

            let imageUrl = formData.image_url;
            
            // Upload image if a new file is selected
            if (formData.image_url && typeof formData.image_url !== 'string') {
                const formDataFile = new FormData();
                formDataFile.append('file', formData.image_url);
                formDataFile.append('folder', 'footer');
                
                const uploadResponse = await postAPIForm('file/uploadFile', formDataFile);
                if (uploadResponse?.status === 1) {
                    imageUrl = uploadResponse?.data?.file_url;
                } else {
                    throw new Error('Image upload failed');
                }
            }

            const payload = {
                ...formData,
                image_url: imageUrl,
                position: selectedPosition,
                created_by: 'Admin',
                updated_by: 'Admin'
            };

            if (editingId) {
                payload.id = editingId;
            }

            const response = await postAPI('footer-data/create', payload);
            
            if (response?.status === 1) {
                Swal.fire('Success', response?.msg || 'Footer section saved successfully', 'success');
                fetchFooterSections();
                resetForm();
            } else {
                throw new Error(response?.msg || 'Failed to save footer section');
            }
        } catch (error) {
            console.error('Error saving footer section:', error);
            Swal.fire('Error', error.message || 'Failed to save footer section', 'error');
        } finally {
            setLoading(false);
        }
    };

    // Delete footer section
    const handleDeleteSection = async (id, content) => {
        const result = await Swal.fire({
            title: 'Confirm Delete',
            text: `Are you sure you want to delete "${content}"?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Delete',
            cancelButtonText: 'Cancel'
        });

        if (result.isConfirmed) {
            try {
                const response = await postAPI('footer-data/delete', { id });
                if (response?.status === 1) {
                    Swal.fire('Success', 'Footer section deleted successfully', 'success');
                    fetchFooterSections();
                }
            } catch (error) {
                Swal.fire('Error', 'Failed to delete footer section', 'error');
            }
        }
    };

    const resetForm = () => {
        setFormData({
            section_type: 'text',
            content: '',
            link_url: '',
            icon_class: '',
            image_url: null,
            text_color: '#ffffff',
            font_family: 'Arial',
            font_size: '14',
            font_weight: 'normal',
            position: 1,
            order_index: 1,
            is_active: 1
        });
        setShowForm(false);
        setEditingId(null);
    };

    const currentSections = getSectionsForPosition();

    return (
        <div className="container-fluid py-4">
            <div className="row">
                {/* Position Selection */}
                <div className="col-md-3">
                    <div className="card">
                        <div className="card-body">
                            <div className="card-header text-dark">
                                <h5 className="mb-0">Select Position</h5>
                            </div>
                            <div className="list-group list-group-flush">
                                {availablePositions.map(position => (
                                    <button
                                        key={position.value}
                                        onClick={() => setSelectedPosition(position.value)}
                                        className={`list-group-item list-group-item-action text-start ${
                                            selectedPosition === position.value ? 'active' : ''
                                        }`}
                                    >
                                        <div className="d-flex justify-content-between align-items-center">
                                            <span>{position.label}</span>
                                            {footerSections.some(s => s.position === position.value && s.is_active === 1) && (
                                                <span className="badge bg-success">
                                                    {footerSections.filter(s => s.position === position.value && s.is_active === 1).length}
                                                </span>
                                            )}
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer Section Details */}
                <div className="col-md-9">
                    <div className="card">
                        <div className="card-body">
                            <div className="card-header bg-secondary text-white d-flex justify-content-between align-items-center">
                                <h5 className="mb-0">
                                    {availablePositions.find(p => p.value === selectedPosition)?.label} Sections
                                </h5>
                                <button
                                    className="btn btn-sm btn-light"
                                    onClick={() => setShowForm(!showForm)}
                                >
                                    <i className="fa fa-plus"></i> Add Section
                                </button>
                            </div>
                            {showForm && (
                                <form onSubmit={handleSaveSection} className="mb-4 p-3 bg-light rounded">
                                    <div className="row">
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Section Type *</label>
                                            <select
                                                className="form-select"
                                                name="section_type"
                                                value={formData.section_type}
                                                onChange={handleChange}
                                                required
                                            >
                                                {sectionTypes.map(type => (
                                                    <option key={type.value} value={type.value}>
                                                        {type.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Order Index</label>
                                            <input
                                                type="number"
                                                className="form-control"
                                                name="order_index"
                                                value={formData.order_index}
                                                onChange={handleChange}
                                                min="1"
                                                max="100"
                                            />
                                            <small className="form-text text-muted">Lower numbers appear first</small>
                                        </div>
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label">Content *</label>
                                        <textarea
                                            className="form-control"
                                            name="content"
                                            value={formData.content}
                                            onChange={handleChange}
                                            rows="3"
                                            required
                                            placeholder="Enter content text..."
                                        />
                                    </div>

                                    {(formData.section_type === 'link' || formData.section_type === 'icon_link') && (
                                        <div className="mb-3">
                                            <label className="form-label">Link URL</label>
                                            <input
                                                type="url"
                                                className="form-control"
                                                name="link_url"
                                                value={formData.link_url}
                                                onChange={handleChange}
                                                placeholder="https://example.com"
                                            />
                                        </div>
                                    )}

                                    {formData.section_type === 'icon_link' && (
                                        <div className="mb-3">
                                            <label className="form-label">Icon Class</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="icon_class"
                                                value={formData.icon_class}
                                                onChange={handleChange}
                                                placeholder="fa fa-phone, mdi mdi-email, etc."
                                            />
                                            <small className="form-text text-muted">Font Awesome or Material Design icons</small>
                                        </div>
                                    )}

                                    {formData.section_type === 'image' && (
                                        <div className="mb-3">
                                            <label className="form-label">Image</label>
                                            <input
                                                type="file"
                                                className="form-control"
                                                name="image_url"
                                                onChange={handleChange}
                                                accept="image/*"
                                            />
                                            {formData.image_url && typeof formData.image_url === 'string' && (
                                                <small className="text-info d-block mt-2">
                                                    Current: {formData.image_url}
                                                </small>
                                            )}
                                            {formData.image_url && typeof formData.image_url !== 'string' && (
                                                <small className="text-success d-block mt-2">
                                                    New image selected: {formData.image_url.name}
                                                </small>
                                            )}
                                        </div>
                                    )}

                                    {/* Styling Options */}
                                    <div className="row">
                                        <div className="col-md-3 mb-3">
                                            <label className="form-label">Text Color</label>
                                            <input
                                                type="color"
                                                className="form-control form-control-color"
                                                name="text_color"
                                                value={formData.text_color}
                                                onChange={handleChange}
                                            />
                                        </div>
                                        
                                        <div className="col-md-3 mb-3">
                                            <label className="form-label">Font Family</label>
                                            <select
                                                className="form-select"
                                                name="font_family"
                                                value={formData.font_family}
                                                onChange={handleChange}
                                            >
                                                {fontFamilies.map(font => (
                                                    <option key={font} value={font}>{font}</option>
                                                ))}
                                            </select>
                                        </div>
                                        
                                        <div className="col-md-3 mb-3">
                                            <label className="form-label">Font Size (px)</label>
                                            <input
                                                type="number"
                                                className="form-control"
                                                name="font_size"
                                                value={formData.font_size}
                                                onChange={handleChange}
                                                min="10"
                                                max="72"
                                            />
                                        </div>
                                        
                                        <div className="col-md-3 mb-3">
                                            <label className="form-label">Font Weight</label>
                                            <select
                                                className="form-select"
                                                name="font_weight"
                                                value={formData.font_weight}
                                                onChange={handleChange}
                                            >
                                                {fontWeights.map(weight => (
                                                    <option key={weight.value} value={weight.value}>
                                                        {weight.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                    <div className="d-flex gap-2">
                                        <button type="submit" className="btn btn-success" disabled={loading}>
                                            <i className="fa fa-save"></i> Save Section
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-secondary"
                                            onClick={resetForm}
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </form>
                            )}

                            {/* Current Sections for Selected Position */}
                            {currentSections.length > 0 ? (
                                <div>
                                    <h6 className="mb-3">Current Sections ({currentSections.length})</h6>
                                    <div className="row">
                                        {currentSections.map(section => (
                                            <div key={section.id} className="col-md-6 mb-3">
                                                <div className="card border">
                                                    <div className="card-body">
                                                        <div className="d-flex justify-content-between align-items-start mb-2">
                                                            <span className="badge bg-info">{section.section_type}</span>
                                                            <span className="badge bg-secondary">Order: {section.order_index}</span>
                                                        </div>
                                                        <p className="mb-2">
                                                            <strong>Content:</strong><br />
                                                            <span style={{
                                                                color: section.text_color,
                                                                fontFamily: section.font_family,
                                                                fontSize: `${section.font_size}px`,
                                                                fontWeight: section.font_weight
                                                            }}>
                                                                {section.content}
                                                            </span>
                                                        </p>
                                                        {section.link_url && (
                                                            <p><strong>Link:</strong> <a href={section.link_url} target="_blank" rel="noopener noreferrer">{section.link_url}</a></p>
                                                        )}
                                                        {section.icon_class && (
                                                            <p><strong>Icon:</strong> <i className={section.icon_class}></i> {section.icon_class}</p>
                                                        )}
                                                        <div className="btn-group">
                                                            <button
                                                                className="btn btn-sm btn-primary"
                                                                onClick={() => handleEditSection(section)}
                                                                title="Edit"
                                                            >
                                                                <img src={editIcon} alt="Edit" width="16" height="16" />
                                                            </button>
                                                            <button
                                                                className="btn btn-sm btn-danger"
                                                                onClick={() => handleDeleteSection(section.id, section.content)}
                                                                title="Delete"
                                                            >
                                                                <img src={deleteIcon} alt="Delete" width="16" height="16" />
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                <div className="text-center py-5">
                                    <p className="text-muted">No sections found for this position.</p>
                                    <p className="text-muted">Click "Add Section" to create the first section.</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* All Sections Summary */}
                    <div className="card mt-4">
                        <div className="card-header bg-dark text-white">
                            <h5 className="mb-0">All Footer Sections Summary</h5>
                        </div>
                        <div className="card-body">
                            {loading ? (
                                <div className="text-center py-3">
                                    <div className="spinner-border" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <table className="table table-striped table-hover">
                                        <thead className="table-dark">
                                            <tr>
                                                <th>Position</th>
                                                <th>Type</th>
                                                <th>Content</th>
                                                <th>Link</th>
                                                <th>Order</th>
                                                <th>Status</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {footerSections.length > 0 ? (
                                                footerSections.map(section => (
                                                    <tr key={section.id}>
                                                        <td>
                                                            <span className="badge bg-primary">
                                                                Col {section.position}
                                                            </span>
                                                        </td>
                                                        <td>{section.section_type}</td>
                                                        <td>{section.content?.substring(0, 50)}...</td>
                                                        <td>{section.link_url ? '🔗' : '-'}</td>
                                                        <td>{section.order_index}</td>
                                                        <td>
                                                            <span className={`badge ${section.is_active ? 'bg-success' : 'bg-secondary'}`}>
                                                                {section.is_active ? 'Active' : 'Inactive'}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <button
                                                                className="btn btn-sm btn-primary me-2"
                                                                onClick={() => handleEditSection(section)}
                                                                title="Edit"
                                                            >
                                                                <img src={editIcon} alt="Edit" width="16" height="16" />
                                                            </button>
                                                            <button
                                                                className="btn btn-sm btn-danger"
                                                                onClick={() => handleDeleteSection(section.id, section.content)}
                                                                title="Delete"
                                                            >
                                                                <img src={deleteIcon} alt="Delete" width="16" height="16" />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="7" className="text-center">No footer sections found</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}